import { useEffect, useRef, useState } from "react";
import { EntityId } from "@reduxjs/toolkit";
import { useAppSelector } from "app/hooks";

import { Alert, Grid, LinearProgress } from "@mui/material";

import { playbackSelectors } from "./store/selectors.playback";
import { reIdSelectors } from "features/cases/caseViewer/reId/store/selectors";
import {
    selectInWindowMode,
    selectShowSidebar,
} from "features/core/store/selectors";

import { DossierCapabilityName } from "types/DossierCapabilityName";

import { PlaybackDevice } from "./PlaybackDevice";

export interface PlaybackDeviceGridProps {
    dossierId: EntityId;
    siteId: string;
    drawerWidth: number;
    dossierCapabilityNames: DossierCapabilityName[];
}

const hightCardActionBar = 40;
const bottomSpacingEdgeScreen = 20;

export function PlaybackDeviceGrid(props: PlaybackDeviceGridProps) {
    const gridRef = useRef<HTMLDivElement>(null);
    const [marginToTop, setMarginToTop] = useState<number>(64);
    const showSidebar = useAppSelector(selectShowSidebar);
    const inWindowMode = useAppSelector(selectInWindowMode);

    const selectedVisibleDossierDeviceIds = useAppSelector(
        playbackSelectors.selectVisibleDossierDeviceIds,
    );

    const reIdActiveDossierDeviceId = useAppSelector(
        reIdSelectors.selectActiveDossierDeviceId,
    );

    const dossierDeviceIdsToRender =
        reIdActiveDossierDeviceId !== undefined
            ? [reIdActiveDossierDeviceId]
            : selectedVisibleDossierDeviceIds;

    const fullscreenDossierDeviceId = useAppSelector(
        playbackSelectors.selectFullscreenDossierDeviceId,
    );

    const readyForPlaybackStatistics = useAppSelector(
        playbackSelectors.selectReadyForPlaybackStatistics,
    );
    const numberOfDevicesPerRow = useAppSelector(
        playbackSelectors.selectNumberOfDevicesPerRow,
    );

    useEffect(() => {
        if (gridRef.current) {
            const rect = gridRef.current.getBoundingClientRect();

            // rect.top is the distance from the top of the viewport in pixels
            setMarginToTop(rect.top);
        }
    }, [gridRef.current]);

    return (
        <>
            {!readyForPlaybackStatistics.ready && (
                <Alert
                    elevation={5}
                    severity="info"
                    sx={(theme) => ({
                        position: "fixed",
                        zIndex: theme.zIndex.speedDial - 1,
                        top: theme.spacing(12),
                        marginLeft: theme.spacing(2),
                        marginRight: theme.spacing(2),
                        width: `calc(100% - ${showSidebar ? props.drawerWidth : 0}px - ${theme.spacing(10)})`,
                        transition: theme.transitions.create("width", {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.shortest,
                        }),
                        ".MuiAlert-message": {
                            width: "100%",
                        },
                    })}
                >
                    Buffering recordings...
                    <LinearProgress
                        sx={{ marginTop: 1 }}
                        variant="determinate"
                        value={
                            readyForPlaybackStatistics.percentageReadyForPlayback
                        }
                    />
                </Alert>
            )}
            <Grid
                container
                sx={{
                    marginTop: inWindowMode ? undefined : "0px !important",
                }}
                spacing={2}
                ref={gridRef}
            >
                {dossierDeviceIdsToRender.map((dossierDeviceId) => (
                    <Grid
                        size={{
                            xs: 12 / numberOfDevicesPerRow,
                        }}
                        key={dossierDeviceId}
                        sx={{
                            display:
                                fullscreenDossierDeviceId &&
                                dossierDeviceId !== fullscreenDossierDeviceId
                                    ? "none"
                                    : "block",
                        }}
                        width={
                            dossierDeviceIdsToRender.length === 1 &&
                            inWindowMode
                                ? `calc((100vh - ${marginToTop}px - ${hightCardActionBar}px) * (16/9) - ${bottomSpacingEdgeScreen}px)`
                                : undefined
                        }
                    >
                        <PlaybackDevice
                            key={dossierDeviceId}
                            dossierId={props.dossierId}
                            dossierDeviceId={dossierDeviceId}
                            dossierCapabilityNames={
                                props.dossierCapabilityNames
                            }
                        />
                    </Grid>
                ))}
            </Grid>
        </>
    );
}
